/**
 * @module Navbar
 * @description Sticky navbar: transparent on top, solid on scroll.
 * Mobile hamburger with slide-in overlay.
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
const Navbar = memo(function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile('(max-width: 768px)');
  const navigate = useNavigate();
  const location = useLocation();

  /* Track scroll for transparent to solid transition */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Lock body scroll when mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const navItems = [
    { path: '/', label: 'Home', hash: '' },
    { path: '/paid-restrooms', label: 'Paid Restrooms', hash: '' },
    { path: '/#how-it-works', label: 'How It Works', hash: 'how-it-works' },
    { path: '/pilot', label: 'Pilot Program', hash: '' },
    { path: '/contact', label: 'Contact', hash: '' },
  ];

  const handleNav = useCallback((item) => {
    setMenuOpen(false);
    if (item.hash) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById(item.hash)?.scrollIntoView({ behavior: 'smooth' });
        }, 150);
      } else {
        document.getElementById(item.hash)?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(item.path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [navigate, location]);

  const handleCta = useCallback(() => {
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById('cta-footer')?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } else {
      document.getElementById('cta-footer')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [navigate, location]);

  return (
    <>
      <nav
        className={`fl-navbar ${scrolled ? 'fl-navbar--solid' : 'fl-navbar--transparent'}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="fl-navbar__inner">
          {/* Brand */}
          <Link to="/" className="fl-navbar__brand" onClick={() => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src="/assets/logo.webp" alt="Flush Locks logo" width="40" height="40" />
            <span className="fl-navbar__brand-name">Flush&nbsp;Locks</span>
          </Link>

          {/* Desktop links */}
          {!isMobile && (
            <div className="fl-navbar__links">
              {navItems.map(item => (
                <button
                  key={item.label}
                  className="fl-navbar__link"
                  onClick={() => handleNav(item)}
                >
                  {item.label}
                </button>
              ))}
              <button className="fl-navbar__link fl-navbar__link--cta" onClick={handleCta}>
                Get Started
              </button>
            </div>
          )}

          {/* Mobile toggle */}
          {isMobile && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className="fl-navbar__toggle"
                onClick={() => setMenuOpen(v => !v)}
                aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={menuOpen}
              >
                {menuOpen ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                )}
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile overlay menu */}
      {isMobile && (
        <div className={`fl-mobile-menu ${menuOpen ? 'fl-mobile-menu--open' : ''}`} aria-hidden={!menuOpen}>
          {navItems.map(item => (
            <button key={item.label} className="fl-mobile-menu__link" onClick={() => handleNav(item)}>
              {item.label}
            </button>
          ))}
          <button className="fl-mobile-menu__link fl-mobile-menu__link--cta" onClick={handleCta}>
            Get Started
          </button>
        </div>
      )}
    </>
  );
});

export default Navbar;
